/**
 * Resolution of a listing's combined photo + video media order.
 *
 * Listing videos live outside Sharetribe (object storage behind a CDN); the listing only
 * stores object keys in publicData. This module is the single place where those keys become
 * URLs and where the seller-arranged order in publicData.mediaOrder is applied.
 */

export const MEDIA_TYPE_IMAGE = 'image';
export const MEDIA_TYPE_VIDEO = 'video';

export const MAX_LISTING_VIDEOS = 2;
export const MAX_VIDEO_FILE_BYTES = 100 * 1024 * 1024;
export const MAX_VIDEO_DURATION_SEC = 20;
export const ACCEPT_VIDEOS = 'video/mp4,video/webm,video/quicktime,image/gif';

/**
 * Sparse-field entries that every duck feeding a ListingCard must request, on top of its
 * existing 'fields.listing' list. Without these the card receives no video data at all.
 */
export const LISTING_CARD_MEDIA_FIELDS = [
  'publicData.videos',
  'publicData.mediaOrder',
  'publicData.posterImageId',
];

/** Max Sharetribe images requested for ListingCard surfaces (matches carousel slide cap). */
export const LISTING_CARD_IMAGE_LIMIT = 5;

/**
 * Client CDN base. Prefers the CRA-inlined env, then a server-injected runtime value
 * (SSR sets `window.__R2R_VIDEO_CDN_URL__` from process.env so a missing webpack bake
 * still works after the server has the var). In CRA `yarn dev`, SSR does not run —
 * `hydrateVideoCdnUrl` can populate the window var from the API server.
 */
export const applyRuntimeVideoCdnUrl = url => {
  if (typeof window === 'undefined' || !url) {
    return;
  }
  window.__R2R_VIDEO_CDN_URL__ = String(url).replace(/\/+$/, '');
};

export const getVideoCdnBaseUrl = () => {
  const fromEnv = (process.env.REACT_APP_VIDEO_CDN_URL || '').replace(/\/+$/, '');
  if (fromEnv) {
    return fromEnv;
  }
  if (typeof window !== 'undefined' && window.__R2R_VIDEO_CDN_URL__) {
    return String(window.__R2R_VIDEO_CDN_URL__).replace(/\/+$/, '');
  }
  return '';
};

export const mediaKeyToUrl = (key, cdnBaseUrl) => {
  const base = cdnBaseUrl == null ? getVideoCdnBaseUrl() : String(cdnBaseUrl).replace(/\/+$/, '');
  if (!key || !base) {
    return null;
  }
  return `${base}/${String(key).replace(/^\/+/, '')}`;
};

const absoluteOrKeyed = (absolute, key, cdnBaseUrl) => {
  if (typeof absolute === 'string' && /^https?:\/\//i.test(absolute)) {
    return absolute;
  }
  return mediaKeyToUrl(key, cdnBaseUrl);
};

export const resolveVideoUrls = (video, cdnBaseUrl) => ({
  // Prefer absolute URLs from the upload API (server-resolved) when present.
  mp4Url: absoluteOrKeyed(video?.mp4Url, video?.mp4Key, cdnBaseUrl),
  cardMp4Url: absoluteOrKeyed(video?.cardMp4Url, video?.cardMp4Key || video?.mp4Key, cdnBaseUrl),
  posterUrl: absoluteOrKeyed(video?.posterUrl, video?.posterKey, cdnBaseUrl),
  cardPosterUrl: absoluteOrKeyed(
    video?.cardPosterUrl,
    video?.cardPosterKey || video?.posterKey,
    cdnBaseUrl
  ),
});

/** Best URL for seller Media-tab in-tile preview (full, then card). */
export const pickListingVideoPreviewUrl = (video, cdnBaseUrl) => {
  const { mp4Url, cardMp4Url } = resolveVideoUrls(video, cdnBaseUrl);
  return mp4Url || cardMp4Url || null;
};

export const imageMediaRef = uuid => `${MEDIA_TYPE_IMAGE}:${uuid}`;
export const videoMediaRef = id => `${MEDIA_TYPE_VIDEO}:${id}`;

const parseMediaRef = ref => {
  if (typeof ref !== 'string') {
    return null;
  }
  const separatorIndex = ref.indexOf(':');
  if (separatorIndex < 1 || separatorIndex === ref.length - 1) {
    return null;
  }
  return { type: ref.slice(0, separatorIndex), id: ref.slice(separatorIndex + 1) };
};

export const getOrderedListingMedia = (listing, options = {}) => {
  const { cdnBaseUrl } = options;
  const publicData = listing?.attributes?.publicData || {};
  const posterImageId = publicData.posterImageId || null;

  const images = (listing?.images || []).filter(
    image => image?.id?.uuid && image.id.uuid !== posterImageId
  );
  const videos = (Array.isArray(publicData.videos) ? publicData.videos : []).filter(
    video => video?.id && video?.mp4Key
  );

  const imagesById = new Map(images.map(image => [image.id.uuid, image]));
  const videosById = new Map(videos.map(video => [String(video.id), video]));

  const toImageItem = image => ({
    type: MEDIA_TYPE_IMAGE,
    id: image.id.uuid,
    image,
  });
  const toVideoItem = video => ({
    type: MEDIA_TYPE_VIDEO,
    id: String(video.id),
    video: { ...video, ...resolveVideoUrls(video, cdnBaseUrl) },
  });

  const usedImageIds = new Set();
  const usedVideoIds = new Set();
  const items = [];

  const mediaOrder = Array.isArray(publicData.mediaOrder) ? publicData.mediaOrder : [];
  mediaOrder.forEach(ref => {
    const parsed = parseMediaRef(ref);
    if (!parsed) {
      return;
    }
    if (
      parsed.type === MEDIA_TYPE_IMAGE &&
      imagesById.has(parsed.id) &&
      !usedImageIds.has(parsed.id)
    ) {
      usedImageIds.add(parsed.id);
      items.push(toImageItem(imagesById.get(parsed.id)));
    } else if (
      parsed.type === MEDIA_TYPE_VIDEO &&
      videosById.has(parsed.id) &&
      !usedVideoIds.has(parsed.id)
    ) {
      usedVideoIds.add(parsed.id);
      items.push(toVideoItem(videosById.get(parsed.id)));
    }
  });

  // Anything the order does not mention still renders: listings saved before this feature,
  // or edited outside the Photos tab, must not lose media.
  images.forEach(image => {
    if (!usedImageIds.has(image.id.uuid)) {
      items.push(toImageItem(image));
    }
  });
  videos.forEach(video => {
    if (!usedVideoIds.has(String(video.id))) {
      items.push(toVideoItem(video));
    }
  });

  return items;
};

export const getLeadListingMedia = (listing, options) =>
  getOrderedListingMedia(listing, options)[0] || null;

/** Prefer the card-sized poster, then the full poster. */
export const pickLeadVideoPosterUrl = video => video?.cardPosterUrl || video?.posterUrl || null;

/**
 * Resolve what a card/thumbnail should show for a listing's lead media.
 * Callers choose play (`video`) vs still (`posterUrl`) based on surface bandwidth policy.
 *
 * @returns {{ isVideo: boolean, image: Object|null, video: Object|null, posterUrl: string|null }}
 */
export const resolveListingLeadVisual = (listing, options) => {
  if (!listing) {
    return { isVideo: false, image: null, video: null, posterUrl: null };
  }
  const leadMedia = getLeadListingMedia(listing, options);
  if (leadMedia?.type === MEDIA_TYPE_VIDEO) {
    return {
      isVideo: true,
      image: null,
      video: leadMedia.video,
      posterUrl: pickLeadVideoPosterUrl(leadMedia.video),
    };
  }
  return {
    isVideo: false,
    image: leadMedia?.image || listing?.images?.[0] || null,
    video: null,
    posterUrl: null,
  };
};
